import { getAmpConfig } from 'shared/config/amp-config';
import {
  Assignment,
  AssignmentsPayload,
  Brand,
  CKLA_PILOT_DOMAIN,
  CKLA_PILOT_SUBROUTE_URL,
  DesmosAssignment,
} from 'shared/api/assignment-service';
import { RelevantDomains } from 'shared/lib/domains';

export const parseLesson = (
  title = '',
  isRegular?: boolean,
): [lessonNumber: string, lessonTitle: string] => {
  if (isRegular) {
    const split = title.split(':');
    const lessonNumber = split[0].trim();
    const lessonTitle = (split[1] ? split.slice(1).join(':') : split[0]).trim();

    return [lessonNumber, lessonTitle];
  }

  const maxLessonTitleLength = 500;
  let lessonTitle = title;

  if (lessonTitle.length > maxLessonTitleLength) {
    lessonTitle = `${lessonTitle
      .slice(0, maxLessonTitleLength - 1)
      .trimEnd()}...`;
  }

  return ['', lessonTitle];
};

export const parseDomain = (domain: RelevantDomains) => {
  if (domain === CKLA_PILOT_DOMAIN) {
    return domain;
  }
  return domain.split(' ')[0];
};

export const getDomainName = (
  domain: RelevantDomains,
  isCustom = false,
  isWolkthroughActive = false,
) => {
  if (isCustom || domain === 'Slides Custom Created From Scratch') {
    return 'Custom';
  }

  if (isWolkthroughActive) return domain;

  return parseDomain(domain);
};

export const getImagePath = (path?: string) => `${getAmpConfig().apps}/${path}`;

export const getNewWindowLinkAttrs = (brand: Brand) =>
  brand === Brand.CUSTOM
    ? { target: '_blank', rel: 'noreferrer noopener' }
    : {};

export const getAssignmentContentLessonKeys = (assignment: Assignment) =>
  assignment.type === 'Cardstack'
    ? assignment.content.map((content) => content.lessonKey)
    : [assignment.content.lessonKey];

export const getAssignmentContent = (assignment: Assignment) =>
  assignment.type === 'Cardstack' ? assignment.content[0] : assignment.content;

export const isCardstackAssignment = (assignment: AssignmentsPayload) => {
  return (
    'assignedContent' in assignment &&
    assignment.assignedContent[0].type === 'Cardstack'
  );
};

export const isDesmosAssignmentPayload = (assignment: AssignmentsPayload) =>
  assignment.sender === 'desmos' ||
  assignment.assignedContent[0].type === 'desmos' ||
  assignment.assignedContent[0].content.domain
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ?.toLowerCase()
    .includes('desmos');

export const isDesmosAssignment = (
  assignment?: Assignment,
): assignment is DesmosAssignment => assignment?.type === 'desmos';

export const getDomainProgramLink = (assignment?: Assignment) => {
  if (!assignment) return;
  if (isDesmosAssignment(assignment)) {
    // Clean the base URL by removing the ?checkAmplifyLogin=true query parameter, so it can be placed at the end
    let baseUrl = getAmpConfig().desmosTeacher;

    if (assignment.content.domain === CKLA_PILOT_DOMAIN) {
      baseUrl = baseUrl.split('?')[0];
      return `${baseUrl}${CKLA_PILOT_SUBROUTE_URL}`;
    }

    return baseUrl;
  }
};

export const isSSCAssignment = (
  assignment?: Assignment,
): assignment is DesmosAssignment => {
  return assignment?.enrollmentType === 'single_session';
};
